/** @jsx jsx */

import {
  Fragment as _,
  useState,
  useContext,
  useRef,
  useEffect,
  createContext,
  ReactElement,
  memo,
  useMemo,
} from 'react';
import { jsx } from '@emotion/core';
import { interactionContext as _interactionContext } from './interactionContext';
import { setScore, getScore } from 'services/scoring';
import { unlockInteraction } from 'services/unlockInteraction';
import Feedback from 'components/lib/Feedback';
// import { findByKeyVal_V2 } from 'services/findByKeyVal';
import { State } from 'store/store';
import { useSelector, useDispatch } from 'react-redux';
import { updateScoreAction } from 'actions/updateScoreAction';
import { addScormResponseAction } from 'actions/addScormResponseAction';
import { useScoringQuizzes } from 'services/apiHelpers/useScoringQuizzes';
// import getTopics from 'services/apiHelpers/getTopics';
import Modal from 'components/UI/Modal';

const multichoiceRenderCount = {};

enum SubmitState {
  Initial = 0,
  Resolved = 2,
}

interface MultichoiceProps {
  children: ReactElement[] | ReactElement | string | null;
}

interface MultichoiceContext {
  selected: number | string[];
  updateSelected: (optionId: number | string) => void;
  correct: number | string[];
  isMultiSelect: boolean;
  answerCount: number;
  isLive: boolean;
  randomizeOptions?: boolean;
  submit: SubmitState;
  onSubmit: () => void;
}

const MultichoiceContext = createContext<MultichoiceContext | undefined>(
  undefined
);

// keeping this local to the session
// if the interaction is set to score &&
// if the score is not undefined ( has value )
// true
const sessionLock = {};
const attempts = {};

const randomAnswers = {};
const getRandomAnswers = (id, Answers) => {
  if (!randomAnswers[id]) {
    randomAnswers[id] = [...Answers].sort(() => Math.random() - 0.5);
  }

  return randomAnswers[id];
};

const Multichoice = (props: MultichoiceProps) => {
  const interactionContext = useContext(_interactionContext);

  const { id: interactionContextId } = interactionContext;
  let {
    numberOfAttempts,
    correctOptions = '',
    score,
    randomizeOptions = false,
    useFeedback = true,
    useMediaInFeedback = true,
    useUniqueOptionFeedback,
    showAttemptNumber,
    showCorrectAnswer,
  } = interactionContext.props;

  // state (s)
  //
  const [selected, setSelected] = useState<any[]>([]);
  const [submit, setSubmit] = useState(
    sessionLock[interactionContextId]
      ? SubmitState.Resolved
      : SubmitState.Initial
  );
  const [feedback, setFeedback] = useState(false);

  const scoringQuizzes = useScoringQuizzes();
  const { scoreAll } = useSelector((state: State) => {
    return Object.values(state.blocks).find(b => b.type === 'Module')
      ?.props as any;
  });

  sessionLock[interactionContextId] =
    attempts[interactionContextId] === numberOfAttempts &&
    score &&
    getScore(interactionContextId) !== undefined;

  const dispatch = useDispatch();

  const { children } = props as any;
  const Question = children[0];
  let Answers = children[2];
  const SubmitBtn = children[1];

  Answers = Answers.props.children;

  // refs
  const multichoice = useRef<HTMLFormElement>(null);

  if (!attempts[interactionContextId]) {
    attempts[interactionContextId] = 0;
  }
  const isCorrect = useRef(false);

  // correctOptions array from string
  correctOptions = correctOptions
    .split(/,/g)
    .filter(entry => entry !== '')
    .sort();

  // Correct contains an array of ids of correct answers
  const correct = correctOptions.map((o, i) => {
    // .props.children
    return Answers[o - 1]?.props?.id;
  });
  const isMultiCorrect = correct.length > 1;
  const isLive = selected.length && submit === 0;

  // Toggle options
  const updateSelected = optionId => {
    let _selected;

    if (isMultiCorrect) {
      if (selected.includes(optionId)) {
        // Toggle off
        _selected = selected.filter(o => optionId !== o);
      } else {
        // Toggle on
        _selected = [...selected, optionId];
      }
    } else {
      _selected = selected[0] === optionId ? [] : [optionId];
    }

    // Maintain "correct" state
    isCorrect.current = _selected.sort().join() === correct.sort().join();

    // console.log({ optionId, _selected });

    // updating setScore for old ways ( display results )
    setScore(interactionContextId, isCorrect.current);
    //
    //

    setSelected(_selected);
  };

  const showFeedback = correct => {
    useFeedback && setFeedback(true);

    if (window.dev()) {
      return;
    }

    if (
      score &&
      (correct || attempts[interactionContextId] >= numberOfAttempts)
    ) {
      const response = selected
        .map(s => {
          let result = '-1';
          Answers.forEach((a, i) => {
            if (s === a.props.id) {
              result = `${i + 1}`;
              return;
            }
          });
          return result;
        })
        .sort()
        .join(',');

      const action = {
        id: interactionContextId,
        type: 'fill-in',
        reportable: 'multichoice' as 'multichoice',
        response,
        result: correct,
      };

      // dispatch if there's no scoringQuizzes etc
      let shouldDispatch = true;
      if (scoringQuizzes.length) {
        shouldDispatch = false;
      }

      if (shouldDispatch) {
        dispatch(updateScoreAction(interactionContextId, correct));
        dispatch(addScormResponseAction(action));
      }
    }

    //

    // we do our unlocking here if !useFeedback
    //
    const unlock =
      isCorrect.current || attempts[interactionContextId] === numberOfAttempts;

    if (!useFeedback && unlock) {
      setSubmit(SubmitState.Resolved);
      unlockInteraction(interactionContextId);
    }
  };

  const onSubmit = () => {
    attempts[interactionContextId] += 1;
    showFeedback(isCorrect.current);
  };

  const onClose = () => {
    setFeedback(false);
  };

  // effects
  useEffect(() => {
    if (!window.dev) {
      return;
    }

    if (
      parent.multichoiceIsLive &&
      parent.multichoiceIsLive[interactionContextId]
    ) {
      parent.multichoiceIsLive[interactionContextId]({
        isLive: !!isLive,
        isCorrect: isCorrect.current,
      });
    }

    if (parent.showFeedback) {
      parent.showFeedback[interactionContextId] = correct => {
        if (useUniqueOptionFeedback && !isMultiCorrect && !isLive) {
          return alert('No options have been selected');
        }

        if (correct === null) {
          return setSelected([]);
        }

        if (correct === 'attempt') {
          // attemptNumber.current = 1;
          // showFeedback((isCorrect.current = false));
          // return;
        }

        if (feedback) {
          setFeedback(false);
          requestAnimationFrame(() =>
            showFeedback((isCorrect.current = correct))
          );
          //
          return;
        }

        showFeedback((isCorrect.current = correct));
      };
    }
  });

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const content = Array.from(
      multichoice.current!.querySelectorAll('label')
    ) as any;

    const answerIds = Answers.map((answer: any) => answer.props.id);

    const answers = content.map((t: any) => t.innerText);

    const alphabetized = selected.map((entry: any) => {
      return 'abcdefghijklmnopqrstuvwxyz'.split('')[answerIds.indexOf(entry)];
    });
  });

  let parsedIsCorrect;

  if (
    !useUniqueOptionFeedback &&
    attempts[interactionContextId] < numberOfAttempts
  ) {
    parsedIsCorrect = isCorrect.current || 'attempt';
  } else {
    parsedIsCorrect = isCorrect.current;
  }

  return (
    <MultichoiceContext.Provider
      value={{
        isMultiSelect: isMultiCorrect,
        selected,
        isLive: !!isLive,
        correct,
        updateSelected,
        submit,
        answerCount: Answers.length || 1,
        randomizeOptions,
        onSubmit: () => {
          attempts[interactionContextId] += 1;
          showFeedback(isCorrect.current);
        },
      }}
    >
      <form ref={multichoice} onSubmit={e => e.preventDefault()}>
        {Question}

        {window.dev && !window.dev() && randomizeOptions
          ? getRandomAnswers(interactionContextId, Answers)
          : Answers}
        {SubmitBtn}
      </form>

      {feedback && (
        <Feedback
          id={
            !isMultiCorrect
              ? !useUniqueOptionFeedback
                ? interactionContextId
                : selected[0]
              : interactionContextId
          }
          correct={window.dev() ? isCorrect.current : parsedIsCorrect}
          useAttemptFeedback={!useUniqueOptionFeedback}
          attempt={attempts[interactionContextId]}
          numberOfAttempts={numberOfAttempts}
          showAttemptNumber={showAttemptNumber}
          useMediaInFeedback={useMediaInFeedback}
          onOpened={(...$) => {
            if (window.dev()) {
              return;
            }
            // if useFeedback we'll unlock the interaction when opened
            // to not stutter the modal fade in effect with a reRender
            //
            const unlock =
              isCorrect.current ||
              attempts[interactionContextId] === numberOfAttempts;

            if (unlock) {
              setSubmit(SubmitState.Resolved);
              unlockInteraction(interactionContextId);
            }
          }}
          onClose={() => {
            // we were previously setting the submit button to get the focus back
            // seems like it's not the behaviour we want in the end
            // we'll set focus back to the sectionInView but if it scrolls away
            // that scrolled to section will get its focus

            const tick = setInterval(() => {
              const submitButtonElement: any = document.querySelector(
                `#i${SubmitBtn?.props?.id}`
              );

              if (submitButtonElement) {
                submitButtonElement.focus();

                clearInterval(tick);
              }
            }, 0);

            onClose();
          }}
          {...interactionContext.props}
        />
      )}
    </MultichoiceContext.Provider>
  );
};

export default Multichoice;

export { MultichoiceContext };
